import { Component, computed, inject, input, output } from '@angular/core';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerI18n, NgbDatepickerModule, NgbDateStruct, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendarDays, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { ItalianDatepickerI18nService } from '../../services/italian-datepicker-i18n.service';

@Component({
	selector: 'app-datepicker-range-popup',
	standalone: true,
	imports: [NgbDatepickerModule, FormsModule, FontAwesomeModule, NgbTooltipModule],
	templateUrl: './datepicker-range-popup.component.html',
	styleUrl: './datepicker-range-popup.component.scss',
	providers: [{ provide: NgbDatepickerI18n, useClass: ItalianDatepickerI18nService },]
})
export class NgbdDatepickerRangePopup {
	calendar = inject(NgbCalendar);
	formatter = inject(NgbDateParserFormatter);

	faCalendarDays = faCalendarDays;
	faCaretDown = faCaretDown;

	hoveredDate: NgbDate | null = null;
	fromDate: NgbDate = this.getPreviousDate(25);
	toDate: NgbDate | null = this.calendar.getToday();

	minDate = input<Date>();

	dateRangeChange = output<string>();

	startDate = computed(() => {
		const date = this.minDate();
		if (date) {
			return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
		}
		// Return 1900-01-01 if date is not set
		return new NgbDate(1900, 1, 1);
	});

	onDateSelection(date: NgbDate) {
		// primo click
		if (!this.fromDate && !this.toDate) {
			this.fromDate = date;
			return;
		}

		// secondo click valido
		if (this.fromDate && !this.toDate) {

			// ✅ stessa data → range singolo giorno
			if (date.equals(this.fromDate)) {
				this.toDate = date;
				return;
			}

			// ✅ data successiva → chiudi range
			if (date.after(this.fromDate)) {
				this.toDate = date;
				return;
			}

			// ✅ data precedente → riparti
			this.fromDate = date;
			return;
		}

		// terzo click → reset
		this.fromDate = date;
		this.toDate = null;
	}

	onConfirm() {
		if (this.fromDate && this.toDate) {
			this.dateRangeChange.emit(this.outputFormatedRange(this.fromDate, this.toDate));
		}
	}

	isHovered(date: NgbDate) {
		return (
			this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate)
		);
	}

	isInside(date: NgbDate) {
		return this.toDate && date.after(this.fromDate!) && date.before(this.toDate);
	}

	isRange(date: NgbDate) {
		return (
			date.equals(this.fromDate!) ||
			(this.toDate && date.equals(this.toDate)) ||
			this.isInside(date)
		);
	}

	isStartDate(date: NgbDate) {
		const startDate = this.startDate();
		return date.equals(startDate);
	}

	formatRange(from: NgbDate | null, to: NgbDate | null): string {
		const format = (d: NgbDate) => `${d.day.toString().padStart(2, '0')}/${d.month.toString().padStart(2, '0')}/${d.year}`;
		const today = this.calendar.getToday();

		if (from && to) {
			const isToday =
				to.day === today.day && to.month === today.month && to.year === today.year;
			return `${format(from)} - ${isToday ? 'oggi' : format(to)}`;
		}

		if (from && !to) return `${format(from)} - ...`;

		return '';
	}

	resetDates() {
		this.fromDate = this.getPreviousDate(25);
		this.toDate = this.calendar.getToday();
		this.dateRangeChange.emit(this.outputFormatedRange(this.fromDate, this.toDate));
	}

	outputFormatedRange(from: NgbDate, to: NgbDate) {
		const format = (d: NgbDate) => `${d.year}-${d.month.toString().padStart(2, '0')}-${d.day.toString().padStart(2, '0')}`;
		return `${format(from)} - ${format(to)}`
	}

	getTooltipForDate(date: NgbDateStruct): string | null {
		const currentDate = new NgbDate(date.year, date.month, date.day);
		const startDate = this.startDate();

		// Se la data coincide con l'inizio della licenza
		if (currentDate.equals(startDate)) {
			return `Data inizio licenza.`;
		}

		// Nessun tooltip
		return null;
	}

	private getPreviousDate(day: number): NgbDate {
		const today = this.calendar.getToday();
		let year = today.year;
		let month = today.month;

		if (today.day < day) {
			// vai al mese precedente
			month -= 1;
			if (month === 0) {
				month = 12;
				year -= 1;
			}
		}

		// Verifica se il giorno richiesto esiste nel mese selezionato
		const lastDayOfMonth = new Date(year, month, 0).getDate();
		const validDay = Math.min(day, lastDayOfMonth);

		return new NgbDate(year, month, validDay);
	}
}
